// CleaningDetails.tsx
// Emerald luxury details page — Black × Green × White
// Years tab added, modes disabled by rateType, bedroom picker replaces guests

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { RootState, AppDispatch } from "../../../../redux/store";

import { listenToCleaningData } from "../../../../redux/cleaningSlice";
import {
  listenToCleaningListingAvailability,
  listenToCleaningListingAvailabilityByHours,
  listenToCleaningListingRequests,
  listenToCleaningListingReservations,
  upsertCleaningRequest,
} from "../../../../redux/cleaningReservationSlice";
import { fetchCleaningRatingsByListing } from "../../../../redux/cleaningRatingSlice";
import { listenToUserPayouts } from "../../../../redux/payoutsSlice";
import { makeThreadId } from "../../../../redux/iMessageSlice";
import { useCurrency } from "@cosmoola/react-cosmoola-js";
import { GoogleMap, Marker } from "@react-google-maps/api";

import {
  Star, X, ChevronLeft, ChevronRight, Clock, Calendar,
  Check, ArrowLeft, MapPin, Award, Crown, Users,
  MessageCircle, Share2, Heart, Grid,
  AlertCircle, Zap, Minus, Plus,
} from "lucide-react";
import dayjs from "dayjs";

import { cleaningAmenities, cleaningSafetyCategories, cleaningRulesCategories, CategoryItem } from "../../../../CATEGORIES/Categories";

/* ═══════════════════════════════════════════════════════════
   DESIGN TOKENS
═══════════════════════════════════════════════════════════ */
const T = {
  green:        "#16a34a",
  greenLight:   "#4ade80",
  greenDark:    "#14532d",
  greenGrad:    "linear-gradient(135deg,#4ade80 0%,#16a34a 40%,#14532d 100%)",
  greenSubtle:  "rgba(22,163,74,0.10)",
  greenBorder:  "rgba(22,163,74,0.35)",
  black:        "#0A0A0A",
  carbon:       "#111111",
  onyx:         "#1A1A1A",
  graphite:     "#252525",
  pewter:       "#3A3A3A",
  smoke:        "#6B6B6B",
  silver:       "#A8A8A8",
  pearl:        "#E8E8E8",
  white:        "#FFFFFF",
  red:          "#CC0000",
  cream:        "#FAFAF8",
  offwhite:     "#F5F5F3",
  amber:        "#f59e0b",
  serif:        "'Cormorant Garamond', 'Georgia', serif",
  sans:         "'Montserrat', 'Helvetica Neue', sans-serif",
};

const GREEN_GRAD  = T.greenGrad;
const MAX_WIDTH   = 1340;
const DESKTOP_MIN = 900;

// bedroom amenity ids (Entire Home=1, 1Bed=2 ... 5+Bed=6)
const BEDROOM_AMENITY_IDS = [1, 2, 3, 4, 5, 6];

/* ═══════════════════════════════════════════════════════════
   HELPERS
═══════════════════════════════════════════════════════════ */
const formatRelativeDate = (dateStr?: string): string => {
  if (!dateStr) return "";
  const now = dayjs(); const d = dayjs(dateStr);
  const years = now.diff(d,"year"); if (years >= 1) return `${years}y ago`;
  const months = now.diff(d,"month"); if (months >= 1) return `${months}mo ago`;
  const weeks = now.diff(d,"week"); if (weeks >= 1) return `${weeks}w ago`;
  const days = now.diff(d,"day"); if (days >= 1) return `${days}d ago`;
  return "Today";
};

const renderStars = (rating: number) => (
  <div style={{ display:"flex",gap:2,marginTop:3 }}>
    {Array.from({length:5}).map((_,i) => (
      <Star key={i} size={12} fill={i < rating ? T.green : "transparent"} color={i < rating ? T.green : T.silver} strokeWidth={1.5} />
    ))}
  </div>
);

/* ═══════════════════════════════════════════════════════════
   HOST CLEANING LISTINGS INLINE
═══════════════════════════════════════════════════════════ */
const HostCleaningHomes: React.FC<{ hostId: string }> = ({ hostId }) => {
  const navigate  = useNavigate();
  const listings  = useSelector((s: RootState) => s.cleaning.listings);
  const { convertRaw, symbol } = useCurrency();
  const hostListings = listings.filter((l: any) => l.hostId === hostId).slice(0, 4);

  if (!hostListings.length) return <p style={{ color:T.smoke,fontSize:14 }}>No other listings from this cleaner.</p>;

  return (
    <div style={{ display:"flex",gap:16,overflowX:"auto",paddingBottom:8,scrollbarWidth:"none" }}>
      {hostListings.map((l: any) => (
        <div key={l.listingId} onClick={() => navigate(`/cleaningdetails/${l.listingId}`)}
          style={{ minWidth:220,flexShrink:0,borderRadius:16,overflow:"hidden",background:T.onyx,border:`1px solid ${T.graphite}`,cursor:"pointer",transition:"transform .2s",position:"relative" }}
          onMouseEnter={e => (e.currentTarget as HTMLDivElement).style.transform = "translateY(-3px)"}
          onMouseLeave={e => (e.currentTarget as HTMLDivElement).style.transform = "translateY(0)"}>
          <img src={l.photos?.[0] || "https://via.placeholder.com/220x140/111/333?text=Cleaner"} alt=""
            style={{ width:"100%",height:140,objectFit:"cover",display:"block" }} />
          <div style={{ padding:"10px 12px" }}>
            <div style={{ fontSize:13,fontWeight:700,color:T.pearl,marginBottom:4,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis" }}>
              {l.title || l.address?.city}
            </div>
            <div style={{ fontSize:12,color:T.green,fontWeight:600 }}>
              {symbol}{Number(convertRaw(l.rate ?? 0,"EUR")).toLocaleString()} / {l.rateType === "perHour" ? "hr" : l.rateType === "perDay" ? "day" : l.rateType === "perWeek" ? "week" : l.rateType === "perMonth" ? "month" : "year"}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════ */
export default function CleaningDetails() {
  const { listingId } = useParams<{ listingId: string }>();
  const dispatch      = useDispatch<AppDispatch>();
  const navigate      = useNavigate();
  const [searchParams] = useSearchParams();

  const listing           = useSelector((s: RootState) => s.cleaning.currentListing);
  const authUser          = useSelector((s: RootState) => s.auth.user);
  const cleaningRatings   = useSelector((s: RootState) => s.cleaningRatings.ratings[listingId || ""] || []);
  const allUsers          = useSelector((s: RootState) => s.auth.allUsers);
  const reservations      = useSelector((s: RootState) => s.cleaningReservations.listingReservations);
  const listingRequests   = useSelector((s: RootState) => s.cleaningReservations.listingReservationRequests);
  const listingAvailability = useSelector((s: RootState) => s.cleaningReservations.listingAvailability);
  const hourAvailability  = useSelector((s: RootState) => s.cleaningReservations.hourAvailability);
  const credits           = useSelector((s: RootState) => s.payouts.credits);
  const user              = useSelector((s: RootState) => s.auth.user);
  const userId            = user?.userId;
  const { available }     = listingAvailability || { available: true };
  const { convertRaw, symbol } = useCurrency();
  const summaries         = useSelector((s: RootState) => s.cleaningRatings.summaries);
  const listingSummary    = summaries?.[listingId ?? ""];

  /* ── responsive ── */
  const [isDesktop, setIsDesktop] = useState(typeof window !== "undefined" && window.innerWidth >= DESKTOP_MIN);
  const isMobile = !isDesktop;
  useEffect(() => {
    const r = () => setIsDesktop(window.innerWidth >= DESKTOP_MIN);
    window.addEventListener("resize", r); return () => window.removeEventListener("resize", r);
  }, []);

  /* ── scroll ── */
  const [scrolled, setScrolled]   = useState(false);
  const reserveBtnRef             = useRef<HTMLButtonElement | null>(null);
  const prevCheckOutRef           = useRef<string | null>(null);
  const loaderRef                 = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const s = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", s); return () => window.removeEventListener("scroll", s);
  }, []);

  /* ── data loading ── */
  useEffect(() => {
    if (!listingId) return;
    const u = dispatch(listenToCleaningData(listingId));
    return () => { if (typeof u === "function") u(); };
  }, [dispatch, listingId]);

  useEffect(() => {
    if (listingId) {
      dispatch(listenToCleaningListingReservations(listingId));
      dispatch(listenToCleaningListingRequests(listingId));
      dispatch(fetchCleaningRatingsByListing(listingId));
    }
    if (userId) dispatch(listenToUserPayouts(userId));
  }, [dispatch, listingId, userId]);

  /* ── rate type ── */
  const rateType     = listing?.rateType as string | undefined;
  const isPerHour    = rateType === "perHour";
  const isPerDay     = rateType === "perDay";
  const isPerWeek    = rateType === "perWeek";
  const isPerMonth   = rateType === "perMonth";
  const isPerYear    = rateType === "perYear";

  /* ── which tabs are allowed based on rateType ──
     perHour  → only hours
     perDay   → only dates (calendar)
     perWeek  → dates + flex
     perMonth → months + flex
     perYear  → years
  ── */
  const canUseHours  = isPerHour;
  const canUseDates  = isPerDay || isPerWeek;
  const canUseFlex   = isPerWeek || isPerMonth;
  const canUseMonths = isPerMonth;
  const canUseYears  = isPerYear;

  type TabKey = "dates" | "hours" | "months" | "flex" | "years";

  // default tab to the correct one for this listing
  const defaultTab = (): TabKey => {
    if (isPerHour)  return "hours";
    if (isPerMonth) return "months";
    if (isPerYear)  return "years";
    return "dates";
  };

  /* ── calendar state ── */
  const [tab, setTab]               = useState<TabKey>(defaultTab);
  const [checkIn, setCheckIn]       = useState<string|null>(null);
  const [checkOut, setCheckOut]     = useState<string|null>(null);
  const [startHour, setStartHour]   = useState<number|null>(null);
  const [endHour, setEndHour]       = useState<number|null>(null);
  const [monthOffset, setMonthOffset] = useState(0);
  const [flexSelected, setFlexSelected] = useState<"4h"|"8h"|"fullday"|null>(null);
  const [monthsToShow, setMonthsToShow] = useState(6);
  const [selectedMonth, setSelectedMonth] = useState<number|number[]|null>(null);
  const [selectedTimes, setSelectedTimes] = useState<string[]>([]);
  const [years, setYears]           = useState<number>(1);

  // reset tab when listing loads
  useEffect(() => {
    if (listing?.rateType) setTab(defaultTab());
  }, [listing?.rateType]);

  // auto-set checkIn/checkOut for years
  useEffect(() => {
    if (tab !== "years") return;
    setCheckIn(dayjs().format("YYYY-MM-DD"));
    setCheckOut(dayjs().add(years, "year").format("YYYY-MM-DD"));
  }, [years, tab]);

  /* ── bedroom filter (replaces guests) ── */
  const [selectedBedroomId, setSelectedBedroomId] = useState<number|null>(null);

  const bedroomPreview = selectedBedroomId
    ? cleaningAmenities.find(a => a.id === selectedBedroomId)?.name ?? "Any size"
    : "Any size";

  /* ── ui state ── */
  const [warningMsg, setWarningMsg]             = useState<string|null>(null);
  const [missingItems, setMissingItems]         = useState<string[]>([]);
  const [showMissingModal, setShowMissingModal] = useState(false);
  const [showHoursModal, setShowHoursModal]     = useState(false);
  const [expandedSections, setExpandedSections] = useState<Record<string,boolean>>({});
  const [galleryOpen, setGalleryOpen]           = useState(false);
  const [galleryIdx, setGalleryIdx]             = useState(0);
  const [likedListing, setLikedListing]         = useState(false);
  const [shareToast, setShareToast]             = useState(false);
  const photos = listing?.photos ?? [];

  /* ── search params ── */
  useEffect(() => {
    const ci = searchParams.get("checkIn"); const co = searchParams.get("checkOut");
    if (ci && co) { setCheckIn(ci); setCheckOut(co); }
    const sh = searchParams.get("startHour"); const eh = searchParams.get("endHour");
    if (sh) setStartHour(Number(sh));
    if (eh) setEndHour(Number(eh));
    const bid = searchParams.get("bedroomAmenityId");
    if (bid) setSelectedBedroomId(Number(bid));
  }, []);

  


  /* ── availability ── */
  useEffect(() => {
    if (!listingId || !checkIn || !checkOut) return;
    const u = dispatch(listenToCleaningListingAvailability(listingId, checkIn, checkOut));
    return () => { if (u) u(); };
  }, [dispatch, listingId, checkIn, checkOut]);

  useEffect(() => {
    if (!isPerHour || !listingId || !checkIn) return;
    const u = dispatch(listenToCleaningListingAvailabilityByHours({ listingId, date: checkIn }));
    return () => { if (typeof u === "function") u(); };
  }, [dispatch, isPerHour, listingId, checkIn]);

  /* ── warning auto-clear ── */
  useEffect(() => {
    if (!warningMsg) return;
    const t = setTimeout(() => setWarningMsg(null), 3000);
    return () => clearTimeout(t);
  }, [warningMsg]);

  useEffect(() => {
    if (!shareToast) return;
    const t = setTimeout(() => setShareToast(false), 2000);
    return () => clearTimeout(t);
  }, [shareToast]);

  useEffect(() => {
    if (!isDesktop) return;
    if (checkOut && prevCheckOutRef.current !== checkOut && reserveBtnRef.current) {
      setTimeout(() => reserveBtnRef.current?.scrollIntoView({ behavior:"smooth",block:"center" }), 800);
    }
    prevCheckOutRef.current = checkOut;
  }, [checkOut, isDesktop]);

  useEffect(() => {
    if (!loaderRef.current || !isMobile) return;
    const obs = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && monthsToShow < 12) setMonthsToShow(p => p + 2);
    }, { threshold: 1 });
    obs.observe(loaderRef.current);
    return () => obs.disconnect();
  }, [monthsToShow, isMobile]);

  /* ── reserved dates ── */
/* ── reserved dates ── */
  const reservedDates = useMemo(() => {
    const s = new Set<string>();
    reservations.filter(r => !r.isCancelled).forEach(r => {
      let d = dayjs(r.checkIn);
      while (d.isBefore(dayjs(r.checkOut),"day")) { s.add(d.format("YYYY-MM-DD")); d = d.add(1,"day"); }
    });
    listing?.unavailableDates?.forEach((d: string) => s.add(d));
    return s;
  }, [reservations, listing]);

  const unavailableDates = reservedDates;
  const today = dayjs();

  /* ── auto-set today/tomorrow for dates/flex ── */
  useEffect(() => {
    if (!checkIn && !checkOut && (isPerDay || isPerWeek)) {
      let start = dayjs();
      while (reservedDates.has(start.format("YYYY-MM-DD"))) {
        start = start.add(1, "day");
      }
      let end = start.add(1, "day");
      while (reservedDates.has(end.format("YYYY-MM-DD"))) {
        end = end.add(1, "day");
      }
      setCheckIn(start.format("YYYY-MM-DD"));
      setCheckOut(end.format("YYYY-MM-DD"));
    }
  }, [isPerDay, isPerWeek, reservedDates]);

  /* ── derived ── */
  const host = useMemo(() => ({
    hostId:    listing?.hostId,
    firstName: listing?.hostFirstName ?? "Cleaner",
  }), [listing]);

  const isHostBookingOwnListing = listing?.hostId === userId;

  const cleaningRatingCount = cleaningRatings.length;
  const cleaningAvgRating = useMemo(() => {
    if (!cleaningRatings.length) return 0;
    return cleaningRatings.reduce((s: number, r: any) => s + (r.listingRating || 0), 0) / cleaningRatings.length;
  }, [cleaningRatings]);
  const avgRating   = cleaningRatingCount === 0 ? null : Number(cleaningAvgRating.toFixed(1));
  const reviewCount = cleaningRatingCount;

  const isGuestFavourite = listingSummary
    ? listingSummary.totalReviews >= 5 && listingSummary.avgRating >= 4.5
    : listing?.guestFavourite === true;

  const rateLabel = useMemo(() => {
    if (!listing?.rate) return "";
    const suffix = listing.rateType === "perHour"  ? "/ hr"
      : listing.rateType === "perDay"   ? "/ day"
      : listing.rateType === "perWeek"  ? "/ week"
      : listing.rateType === "perMonth" ? "/ month"
      : "/ year";
    return `${symbol}${Number(convertRaw(listing.rate,"EUR")).toLocaleString()} ${suffix}`;
  }, [listing, symbol, convertRaw]);

  /* ── approve mode ── */
  const isApproveMode = listing?.bookingType?.approveReservations === true;
  const myRequest = isApproveMode && userId ? listingRequests.find((r) => r.guestId === userId) : null;
  const alreadyRequested = !!myRequest && myRequest.isPending && !myRequest.isApproved && !myRequest.isRejected;
  const requestApproved  = !!myRequest && myRequest.isApproved === true;
  const requestDeclined  = !!myRequest && myRequest.isRejected === true;

  const reserveBtnLabel = !user ? "Sign in to Book"
    : requestDeclined    ? "Request Declined"
    : requestApproved    ? "Book Now"
    : alreadyRequested   ? "Already Requested"
    : isApproveMode      ? "Request to Book"
    : "Book Now";

  /* ── HOURS 24h ── */
  const HOURS_24 = Array.from({length:24},(_,i) => `${String(i).padStart(2,"0")}:00`);
  const selectedIndexes = selectedTimes.map(t => HOURS_24.indexOf(t)).sort((a,b)=>a-b);
  const minSel = selectedIndexes[0];
  const maxSel = selectedIndexes[selectedIndexes.length - 1];

  const handleHourClick = (hour: string, index: number, disabled: boolean) => {
    if (disabled) return;
    if (selectedTimes.length === 0) { setSelectedTimes([hour]); return; }
    if (index === minSel - 1) { setSelectedTimes([hour,...selectedTimes]); return; }
    if (index === maxSel + 1) { setSelectedTimes([...selectedTimes,hour]); return; }
    if (index >= minSel && index <= maxSel) { setSelectedTimes(HOURS_24.slice(minSel,index+1)); return; }
    setWarningMsg("Please select consecutive hours.");
  };

  /* ── date helpers ── */
  const isSelected = (d: dayjs.Dayjs) => {
    const k = d.format("YYYY-MM-DD");
    if (!checkIn) return false;
    if (checkIn && !checkOut) return k === checkIn;
    return checkIn && checkOut && dayjs(k).isAfter(checkIn) && dayjs(k).isBefore(checkOut);
  };
  const isEdge = (d: dayjs.Dayjs) =>
    d.format("YYYY-MM-DD") === checkIn || d.format("YYYY-MM-DD") === checkOut;

  const handleDateSelect = (date: dayjs.Dayjs) => {
    const dateStr = date.format("YYYY-MM-DD");
    if (date.isBefore(today,"day")) return;
    if (reservedDates.has(dateStr)) return;
    const minD = listing?.minNights || listing?.minDays || 1;
    const maxD = listing?.maxNights || listing?.maxDays || 365;

    if (!checkIn || date.isBefore(dayjs(checkIn),"day")) {
      const autoOut = dayjs(dateStr).add(minD,"day");
      setCheckIn(dateStr); setCheckOut(autoOut.format("YYYY-MM-DD")); return;
    }
    if (checkIn && checkOut) {
      if (dateStr === checkIn) { setCheckIn(null); setCheckOut(null); return; }
      if (date.isAfter(dayjs(checkIn),"day") && date.isBefore(dayjs(checkOut),"day")) {
        const nights = date.diff(dayjs(checkIn),"day");
        if (nights < minD) { setWarningMsg(`Min booking ${minD} days.`); return; }
        setCheckOut(dateStr); return;
      }
      if (date.isAfter(dayjs(checkOut),"day")) {
        const nights = date.diff(dayjs(checkIn),"day");
        if (nights > maxD) { setWarningMsg(`Max booking ${maxD} days.`); return; }
        setCheckOut(dateStr); return;
      }
    }
    if (checkIn && !checkOut) {
      if (date.isBefore(dayjs(checkIn))) { setCheckIn(dateStr); return; }
      const nights = date.diff(dayjs(checkIn),"day");
      if (nights < minD) { setWarningMsg(`Min booking ${minD} days.`); return; }
      if (nights > maxD) { setWarningMsg(`Max booking ${maxD} days.`); return; }
      setCheckOut(dateStr);
    }
  };

  /* ── extras ── */
  const [selectedExtras, setSelectedExtras] = useState<string[]>([]);
  const toggleExtra = (name: string) =>
    setSelectedExtras(prev => prev.includes(name) ? prev.filter(e => e !== name) : [...prev, name]);

  /* ── getMissingRequirements ── */
  const getMissingRequirements = () => {
    const m: string[] = [];
    if (!user) m.push("Sign in or create an account");
    if (!checkIn) m.push("Select a service date");
    if (isPerHour && selectedTimes.length === 0) m.push("Select booking hours");
    if (!available) m.push("Selected dates are not available");
    if (isHostBookingOwnListing) m.push("You can't book your own listing");
    return m;
  };

  /* ── handleContinue ── */
  const handleContinue = async () => {
    if (!user) { navigate("/AuthSwitch"); return; }
    if (!available) { setWarningMsg("These dates are no longer available."); return; }
    if (isHostBookingOwnListing) { setWarningMsg("You cannot book your own listing."); return; }
    const missing = getMissingRequirements();
    if (missing.length > 0) { setMissingItems(missing); setShowMissingModal(true); return; }
    if (!listingId || !checkIn) return;

    const reservedDuration = checkOut ? dayjs(checkOut).diff(dayjs(checkIn),"day") : 0;
    const totalHours = isPerHour ? selectedTimes.length : (endHour !== null && startHour !== null ? endHour - startHour : 0);

    if (!isApproveMode || requestApproved) {
      navigate(`/cleaningpreview/${userId}/${listingId}?${new URLSearchParams({
        checkIn,
        checkOut:         isPerHour ? checkIn : (checkOut || checkIn),
        reservedDuration: reservedDuration.toString(),
        selectedExtras:   JSON.stringify(selectedExtras),
discount: JSON.stringify(listing?.discountApplied || listing?.discounts || {}),
        credits:          credits?.toString() || "0",
        rateType:         rateType || "",
        selectedTimes:    JSON.stringify(selectedTimes),
        totalHours:       totalHours.toString(),
        startHour:        (startHour ?? "").toString(),
        endHour:          (endHour ?? "").toString(),
        bedroomAmenityId: selectedBedroomId?.toString() || "",
      }).toString()}`);
      return;
    }

    if (!userId || !listing?.hostId) return;
    try {
      const result = await dispatch(upsertCleaningRequest({
        listingId,
        guestId:          userId,
        hostId:           listing.hostId,
        checkIn,
        checkOut:         checkOut || checkIn,
        selectedExtras,
        selectedTimes,
        totalHours,
        reservedDuration,
        rateType:         rateType ?? "",
      })).unwrap();

      navigate(`/imessages/${makeThreadId(userId, listing.hostId)}?${new URLSearchParams({
        requestId:    result.requestId,
        listingId,
        listingTitle: listing.title || "",
        checkIn,
        checkOut:     checkOut || checkIn,
        nights:       reservedDuration.toString(),
        rateType:     rateType || "",
        rate:         listing.rate?.toString() || "",
        requestType:  "cleaningRequest",
      }).toString()}`);
    } catch (err) {
      console.error("Cleaning request error:", err);
      setWarningMsg("Failed to send request. Please try again.");
    }
  };

  const toggleSection = (key: string) =>
    setExpandedSections(prev => ({ ...prev,[key]: !prev[key] }));

  /* ═══════════════════ CALENDAR RENDER ═══════════════════ */

  // desktop — two months side by side with nav arrows
  const renderDesktopCalendar = () => (
    <div>
      <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16 }}>
        <button onClick={() => setMonthOffset(o => Math.max(0, o-1))} disabled={monthOffset===0}
          style={{ width:34,height:34,borderRadius:"50%",border:`1px solid ${T.greenBorder}`,background:T.white,display:"grid",placeItems:"center",cursor:monthOffset===0?"not-allowed":"pointer",opacity:monthOffset===0?.3:1 }}>
          <ChevronLeft size={16}/>
        </button>
        <span style={{ fontSize:13,fontWeight:600,color:T.smoke }}>
          {today.add(monthOffset,"month").format("MMMM YYYY")} — {today.add(monthOffset+1,"month").format("MMMM YYYY")}
        </span>
        <button onClick={() => setMonthOffset(o => o+1)}
          style={{ width:34,height:34,borderRadius:"50%",border:`1px solid ${T.greenBorder}`,background:T.white,display:"grid",placeItems:"center",cursor:"pointer" }}>
          <ChevronRight size={16}/>
        </button>
      </div>
      <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:32 }}>
        {[0,1].map(m => renderCalendarMonth(today.add(monthOffset+m,"month")))}
      </div>
      {checkIn && checkOut && (
        <div style={{ marginTop:14,padding:"10px 14px",background:T.greenSubtle,borderRadius:10,fontWeight:700,color:T.greenDark,fontSize:13,textAlign:"center",border:`1px solid ${T.greenBorder}` }}>
          {dayjs(checkIn).format("MMM D YYYY")} → {dayjs(checkOut).format("MMM D YYYY")} · {dayjs(checkOut).diff(dayjs(checkIn),"day")} days
        </div>
      )}
    </div>
  );

  // mobile — infinite scroll
const renderMobileCalendar = () => (
  <div style={{ paddingRight: 4 }}>
    {Array.from({ length: monthsToShow }, (_, offset) =>
      renderCalendarMonth(today.add(offset, "month"), true)
    )}
    <div ref={loaderRef} style={{ height: 1 }} />
  </div>
);

const renderCalendarMonth = (baseMonth: dayjs.Dayjs, compact = false) => {
  const start       = baseMonth.startOf("month");
  const matrixStart = start.subtract(start.day(), "day");
  const matrix      = Array.from({ length: 42 }).map((_, i) => matrixStart.add(i, "day"));

  return (
    <div key={baseMonth.toString()} style={{ marginBottom: compact ? 24 : 0 }}>
      <div style={{ fontWeight:700,textAlign:"center",marginBottom:10,fontSize:compact?14:16,color:T.green,fontFamily:T.serif,letterSpacing:"0.04em" }}>
        {baseMonth.format("MMMM YYYY")}
      </div>
      <div style={{ display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:2,marginBottom:6,textAlign:"center",fontSize:11,color:T.smoke,fontWeight:600,letterSpacing:"0.05em" }}>
        {["Su","Mo","Tu","We","Th","Fr","Sa"].map(d => <div key={d}>{d}</div>)}
      </div>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", gap: compact ? 2 : 5 }}>
        {matrix.map((d, i) => {
          const dd        = d.format("YYYY-MM-DD");
          const isOther   = d.month() !== baseMonth.month();
          const isPast    = d.isBefore(today, "day");
          const isUnavail = unavailableDates.has(dd);
          const sel       = isSelected(d);
          const edge      = isEdge(d);
          const blocked   = isPast || isUnavail;
          return (
            <div key={i} onClick={() => !blocked && handleDateSelect(d)}
              style={{
                width: "100%",
   aspectRatio:"1/1",
                   borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: compact ? 12 : 14,
                fontWeight: edge ? 800 : 600,
                cursor: blocked ? "not-allowed" : "pointer",
                opacity: isOther ? 0.2 : isUnavail ? 0.35 : 1,
                background: isPast ? "transparent" : isUnavail ? T.pearl : edge ? GREEN_GRAD : sel ? T.greenSubtle : "transparent",
                color: edge ? T.white : isUnavail ? T.silver : isPast ? T.silver : T.black,
                border: edge ? `2px solid ${T.green}` : sel ? `1px solid ${T.greenBorder}` : "1px solid transparent",
                transition: "all .15s",
                boxShadow: edge ? `0 2px 10px ${T.green}40` : "none",
                textDecoration: isUnavail ? "line-through" : "none",
                position: "relative",
                zIndex: 1,
                boxSizing: "border-box",
              }}>
              {d.date()}
            </div>
          );
        })}
      </div>
    </div>
  );
};

  /* ── Hours Tab ── */
  const renderHoursTab = () => {
    const isToday  = checkIn ? dayjs(checkIn).isSame(dayjs(),"day") : false;
    const currentHr = dayjs().hour();
    return (
      <div>
        <p style={{ marginBottom:12,color:T.smoke,fontSize:13 }}>
          {checkIn ? `Service on ${dayjs(checkIn).format("MMM D")} — select consecutive hours` : "Pick a date first, then select hours"}
        </p>
        {/* date picker for perHour */}
        <div style={{ marginBottom:16 }}>
          <input type="date" min={dayjs().format("YYYY-MM-DD")}
            value={checkIn || ""}
            onChange={e => { setCheckIn(e.target.value); setCheckOut(e.target.value); setSelectedTimes([]); }}
            style={{ padding:"10px 14px",borderRadius:10,border:`1.5px solid ${T.greenBorder}`,fontSize:14,fontWeight:600,color:T.black,outline:"none",width:"100%" }}
          />
        </div>
        <div style={{ display:"grid",gridTemplateColumns:"repeat(6,1fr)",gap:8 }}>
          {HOURS_24.map((time,index) => {
            const hourNum   = Number(time.split(":")[0]);
            const isPastHr  = isToday && hourNum < currentHr;
            const active    = selectedTimes.includes(time);
            const disabled  = isPastHr || !!hourAvailability?.disabledHours?.includes(hourNum);
            return (
              <button key={time} disabled={disabled} onClick={() => !disabled && handleHourClick(time,index,disabled)}
                style={{ padding:"10px 0",borderRadius:12,border:active?`2px solid ${T.green}`:`1px solid ${T.pearl}`,
                  background:disabled?T.pearl:active?GREEN_GRAD:T.white,color:disabled?T.silver:active?T.white:T.black,
                  fontWeight:700,fontSize:12,cursor:disabled?"not-allowed":"pointer",opacity:disabled?.4:1,
                  boxShadow:active&&!disabled?`0 0 10px ${T.green}50`:"none",transition:"all .15s" }}>
                {time}
              </button>
            );
          })}
        </div>
        {selectedTimes.length > 0 && (
          <div style={{ marginTop:14,fontSize:13,fontWeight:700,color:T.green,padding:"10px 14px",background:T.greenSubtle,borderRadius:10,border:`1px solid ${T.greenBorder}` }}>
            <Clock size={14} style={{ marginRight:6 }} />
            {selectedTimes.length}h: {selectedTimes[0]} → {selectedTimes[selectedTimes.length-1]}
          </div>
        )}
        {/* Quick select */}
        <div style={{ marginTop:16 }}>
          <div style={{ fontSize:11,fontWeight:700,color:T.smoke,letterSpacing:"0.06em",marginBottom:10 }}>QUICK SELECT</div>
          <div style={{ display:"flex",flexWrap:"wrap",gap:8 }}>
            {[{ label:"Morning 6–12", s:6, e:12 },{ label:"Afternoon 12–18", s:12, e:18 },
              { label:"Full Day 8–18", s:8, e:18 },{ label:"Half Day 8–13", s:8, e:13 }].map(q => (
              <button key={q.label} onClick={() => setSelectedTimes(HOURS_24.slice(q.s, q.e))}
                style={{ padding:"7px 14px",borderRadius:999,border:`1px solid ${T.greenBorder}`,background:T.white,fontWeight:700,fontSize:12,cursor:"pointer",color:T.greenDark }}>
                {q.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  };

  /* ── Months Tab — multi-select ── */
  const renderMonthsTab = () => {
    const selectedArr: number[] = Array.isArray(selectedMonth) ? selectedMonth as any : selectedMonth !== null ? [selectedMonth as number] : [];
    const toggleMonth = (i: number) => {
      const next = selectedArr.includes(i) ? selectedArr.filter(x => x !== i) : [...selectedArr, i];
      setSelectedMonth(next as any);
      if (next.length > 0) {
        const sorted = [...next].sort((a,b) => a-b);
        const start = sorted[0] === 0 ? today.add(1,"day") : today.add(sorted[0],"month").startOf("month");
        const end   = today.add(sorted[sorted.length-1],"month").endOf("month");
        setCheckIn(start.format("YYYY-MM-DD")); setCheckOut(end.format("YYYY-MM-DD"));
      } else { setCheckIn(null); setCheckOut(null); }
    };
    return (
      <div>
        <p style={{ fontSize:12,color:T.smoke,marginBottom:14 }}>Select one or more months</p>
        <div style={{ display:"grid",gridTemplateColumns:`repeat(${isDesktop?4:3},1fr)`,gap:10 }}>
          {Array.from({length:12}).map((_,i) => {
            const m = today.add(i,"month");
            const isSel = selectedArr.includes(i);
            return (
              <button key={i} onClick={() => toggleMonth(i)}
                style={{ padding:"14px 8px",borderRadius:14,border:`1.5px solid ${isSel?T.green:T.pearl}`,fontWeight:700,cursor:"pointer",position:"relative",
                  background:isSel?GREEN_GRAD:T.white,color:isSel?T.white:T.black,transition:"all .2s",boxShadow:isSel?`0 4px 16px ${T.green}40`:"none" }}>
                {isSel && <Check size={11} style={{ position:"absolute",top:6,right:8,color:T.white }}/>}
                <div style={{ fontSize:15,color:isSel?T.white:T.green,marginBottom:2 }}>{m.format("MMM")}</div>
                <div style={{ fontSize:11,color:isSel?T.white:T.smoke }}>{m.year()}</div>
              </button>
            );
          })}
        </div>
        {selectedArr.length > 0 && (
          <div style={{ marginTop:14,padding:"10px 14px",background:T.greenSubtle,borderRadius:10,fontSize:12,fontWeight:700,color:T.greenDark,border:`1px solid ${T.greenBorder}` }}>
            {selectedArr.length} month{selectedArr.length>1?"s":""} selected
            {checkIn && checkOut && ` · ${dayjs(checkIn).format("MMM D")} → ${dayjs(checkOut).format("MMM D YYYY")}`}
          </div>
        )}
      </div>
    );
  };

  /* ── Years Tab ── */
  const renderYearsTab = () => (
    <div style={{ textAlign:"center",padding:"16px 0" }}>
      <h3 style={{ marginBottom:8,color:T.greenDark,fontFamily:T.serif,fontSize:20 }}>How many years?</h3>
      <p style={{ fontSize:12,color:T.smoke,marginBottom:28 }}>Starting from today</p>
      <div style={{ display:"flex",alignItems:"center",justifyContent:"center",gap:28 }}>
        <button onClick={() => setYears(y => Math.max(1, y - 1))}
          style={{ width:44,height:44,borderRadius:"50%",border:`1.5px solid ${T.green}`,background:T.white,color:T.green,cursor:"pointer",display:"grid",placeItems:"center" }}>
          <Minus size={18} />
        </button>
        <div style={{ minWidth:80 }}>
          <div style={{ fontSize:56,fontWeight:800,color:T.greenDark,lineHeight:1,fontFamily:T.serif }}>{years}</div>
          <div style={{ fontSize:13,color:T.smoke,marginTop:4 }}>{years === 1 ? "year" : "years"}</div>
        </div>
        <button onClick={() => setYears(y => Math.min(10, y + 1))}
          style={{ width:44,height:44,borderRadius:"50%",border:"none",background:GREEN_GRAD,color:T.white,cursor:"pointer",display:"grid",placeItems:"center" }}>
          <Plus size={18} />
        </button>
      </div>
      <div style={{ marginTop:24,padding:"12px 20px",background:T.greenSubtle,borderRadius:12,fontSize:13,fontWeight:700,color:T.greenDark,border:`1px solid ${T.greenBorder}` }}>
        {dayjs().format("MMM D YYYY")} → {dayjs().add(years, "year").format("MMM D YYYY")}
      </div>
    </div>
  );

  /* ── Flex Tab ── */
  const renderFlexTab = () => {
    const options = [
      { key:"4h",   label:"4 Hours",  fn:()=>{ const d=today.add(1,"day").format("YYYY-MM-DD"); setCheckIn(d); setCheckOut(d); setStartHour(9); setEndHour(13); setFlexSelected("4h"); } },
      { key:"8h",   label:"8 Hours",  fn:()=>{ const d=today.add(1,"day").format("YYYY-MM-DD"); setCheckIn(d); setCheckOut(d); setStartHour(8); setEndHour(16); setFlexSelected("8h"); } },
      { key:"fullday",label:"Full Day",fn:()=>{ setCheckIn(today.add(1,"day").format("YYYY-MM-DD")); setCheckOut(today.add(2,"day").format("YYYY-MM-DD")); setFlexSelected("fullday"); } },
      { key:"week", label:"1 Week",   fn:()=>{ setCheckIn(today.add(1,"day").format("YYYY-MM-DD")); setCheckOut(today.add(8,"day").format("YYYY-MM-DD")); setFlexSelected("4h" as any); } },
    ];
    return (
      <div style={{ display:"flex",gap:12,flexWrap:"wrap" }}>
        {options.map(x => {
          const active = flexSelected === x.key;
          return (
            <button key={x.key} onClick={x.fn}
              style={{ padding:"12px 24px",borderRadius:999,border:`1px solid ${active?T.green:T.pearl}`,fontWeight:700,fontSize:14,
                background:active?GREEN_GRAD:T.white,color:active?T.white:T.black,cursor:"pointer",transition:"all .2s",boxShadow:active?`0 4px 14px ${T.green}40`:"none" }}>
              {x.label}
            </button>
          );
        })}
      </div>
    );
  };

  /* ── Calendar Tabs Bar ── */
  const CalendarTabs = () => {
    const tabs: { key: TabKey; label: string; enabled: boolean }[] = [
      { key:"dates",  label:"Dates",    enabled: canUseDates },
      { key:"hours",  label:"Hours",    enabled: canUseHours },
      { key:"months", label:"Months",   enabled: canUseMonths },
      { key:"flex",   label:"Flexible", enabled: canUseFlex },
      { key:"years",  label:"Years",    enabled: canUseYears },
    ];
    return (
      <div style={{ display:"flex",gap:8,marginBottom:20,flexWrap:"wrap" }}>
        {tabs.map(t => {
          const active = tab === t.key;
          return (
            <button key={t.key} disabled={!t.enabled} onClick={() => t.enabled && setTab(t.key)}
              style={{ padding:"8px 16px",borderRadius:999,border:`1px solid ${active?T.green:T.pearl}`,fontWeight:700,fontSize:13,
                background:active?GREEN_GRAD:"transparent",color:active?T.white:T.smoke,
                cursor:!t.enabled?"not-allowed":"pointer",
                opacity:!t.enabled?.25:1,
                transition:"all .2s",letterSpacing:"0.04em" }}>
              {t.label}
            </button>
          );
        })}
      </div>
    );
  };

  /* ── Bedroom Picker ── */
  const BedroomPicker = () => (
    <div>
      <h3 style={{ fontFamily:T.serif,fontSize:22,fontWeight:700,marginBottom:4,color:T.black }}>Property Size</h3>
      <p style={{ fontSize:13,color:T.smoke,marginBottom:18 }}>Select the size of the space to clean</p>
      <div style={{ display:"flex",flexDirection:"column",gap:0 }}>
        {cleaningAmenities.filter(a => BEDROOM_AMENITY_IDS.includes(a.id)).map((a, idx, arr) => {
          const sel = selectedBedroomId === a.id;
          return (
            <div key={a.id} style={{ display:"flex",justifyContent:"space-between",alignItems:"center",padding:"14px 0",borderBottom:idx !== arr.length-1?`1px solid ${T.pearl}`:"none" }}>
              <div style={{ display:"flex",alignItems:"center",gap:12 }}>
                <span style={{ color:sel?T.green:T.smoke,fontSize:22 }}>{a.icon}</span>
                <div>
                  <div style={{ fontWeight:700,fontSize:15,color:sel?T.greenDark:T.black }}>{a.name}</div>
                </div>
              </div>
              <button onClick={() => setSelectedBedroomId(sel ? null : a.id)}
                style={{ width:34,height:34,borderRadius:"50%",border:`1.5px solid ${sel?T.green:T.pearl}`,background:sel?GREEN_GRAD:T.white,color:sel?T.white:T.smoke,display:"grid",placeItems:"center",cursor:"pointer",fontWeight:700,fontSize:14,transition:"all .2s" }}>
                {sel ? <Check size={14}/> : "+"}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );

  /* ── Hours & Bedroom Modal ── */
  const HoursModal = () => {
    if (!showHoursModal) return null;
    return (
      <div onClick={() => setShowHoursModal(false)}
        style={{ position:"fixed",inset:0,background:"rgba(0,0,0,.6)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:5000,backdropFilter:"blur(4px)" }}>
        <div onClick={e=>e.stopPropagation()}
          style={{ background:T.white,borderRadius:20,padding:28,width:"100%",maxWidth:900,maxHeight:"90vh",overflowY:"auto",boxShadow:"0 30px 80px rgba(0,0,0,.3)",display:"flex",gap:24,flexDirection:isDesktop?"row":"column",position:"relative" }}>
          <button onClick={() => setShowHoursModal(false)}
            style={{ position:"absolute",top:16,right:16,background:T.pearl,border:"none",borderRadius:"50%",width:32,height:32,display:"grid",placeItems:"center",cursor:"pointer" }}>
            <X size={16}/>
          </button>

          {/* HOURS PANEL — only for perHour */}
          {isPerHour && (
            <div style={{ flex:1 }}>
              {renderHoursTab()}
            </div>
          )}

          {/* BEDROOM PICKER */}
          <div style={{ flex:1 }}>
            <BedroomPicker />
          </div>
        </div>
      </div>
    );
  };

  /* ── Missing Modal ── */
  const MissingModal = () => {
    if (!showMissingModal) return null;
    return (
      <div onClick={() => setShowMissingModal(false)}
        style={{ position:"fixed",inset:0,background:"rgba(0,0,0,.6)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:6000,backdropFilter:"blur(4px)" }}>
        <div onClick={e=>e.stopPropagation()}
          style={{ background:T.white,borderRadius:20,padding:28,maxWidth:440,width:"90%",boxShadow:"0 30px 80px rgba(0,0,0,.3)" }}>
          <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20 }}>
            <h3 style={{ fontFamily:T.serif,fontSize:22,fontWeight:700,color:T.black }}>Before you continue</h3>
            <button onClick={() => setShowMissingModal(false)} style={{ background:T.pearl,border:"none",borderRadius:"50%",width:32,height:32,display:"grid",placeItems:"center",cursor:"pointer" }}><X size={16}/></button>
          </div>
          {missingItems.map((item,i) => (
            <div key={i} style={{ display:"flex",alignItems:"center",gap:12,padding:"10px 0",borderBottom:i<missingItems.length-1?`1px solid ${T.pearl}`:"none" }}>
              <AlertCircle size={18} color={T.red} />
              <span style={{ fontSize:15,color:T.black }}>{item}</span>
            </div>
          ))}
          <button onClick={() => setShowMissingModal(false)}
            style={{ marginTop:20,width:"100%",padding:14,borderRadius:12,background:GREEN_GRAD,border:"none",fontWeight:700,fontSize:15,cursor:"pointer",color:T.white }}>
            Got it
          </button>
        </div>
      </div>
    );
  };

  /* ── Gallery Lightbox ── */
  const GalleryLightbox = () => {
    if (!galleryOpen) return null;
    return (
      <div style={{ position:"fixed",inset:0,background:"rgba(0,0,0,.96)",zIndex:9000,display:"flex",alignItems:"center",justifyContent:"center" }}>
        <button onClick={() => setGalleryOpen(false)}
          style={{ position:"absolute",top:20,right:24,background:"transparent",border:`1px solid ${T.graphite}`,color:T.pearl,borderRadius:999,padding:"6px 14px",fontSize:13,fontWeight:700,cursor:"pointer",display:"flex",alignItems:"center",gap:6 }}>
          <X size={14}/> Close
        </button>
        <button onClick={() => setGalleryIdx(i => (i-1+photos.length)%photos.length)}
          style={{ position:"absolute",left:20,top:"50%",transform:"translateY(-50%)",background:T.graphite,border:`1px solid ${T.pewter}`,color:T.pearl,borderRadius:"50%",width:44,height:44,display:"grid",placeItems:"center",cursor:"pointer" }}>
          <ChevronLeft size={20}/>
        </button>
        <img src={photos[galleryIdx]} alt="" style={{ maxWidth:"85vw",maxHeight:"85vh",objectFit:"contain",borderRadius:8 }}/>
        <button onClick={() => setGalleryIdx(i => (i+1)%photos.length)}
          style={{ position:"absolute",right:20,top:"50%",transform:"translateY(-50%)",background:T.graphite,border:`1px solid ${T.pewter}`,color:T.pearl,borderRadius:"50%",width:44,height:44,display:"grid",placeItems:"center",cursor:"pointer" }}>
          <ChevronRight size={20}/>
        </button>
        <div style={{ position:"absolute",bottom:24,left:"50%",transform:"translateX(-50%)",color:T.silver,fontSize:13,fontWeight:600 }}>
          {galleryIdx+1} / {photos.length}
        </div>
      </div>
    );
  };

  /* ── Amenity Section ── */
  const renderAmenitySection = (key: string, title: string, allItems: CategoryItem[], enabledIds: number[] = []) => {
    const expanded = expandedSections[key];
    const visible  = expanded ? allItems : allItems.slice(0,8);
    return (
      <section style={S.section}>
        <h2 style={S.sectionTitle}>{title}</h2>
        <div style={S.amenGrid}>
          {visible.map(item => {
            const on = enabledIds.includes(item.id);
            return (
              <div key={item.id} style={{ ...S.amenCard,opacity:on?1:0.32,borderColor:on?T.greenBorder:T.pearl }}>
                <div style={{ color:on?T.green:T.silver,marginBottom:8,display:"flex",justifyContent:"center" }}>{item.icon}</div>
                <div style={{ fontSize:12,fontWeight:600,color:on?T.black:T.smoke,textDecoration:on?"none":"line-through",textAlign:"center" }}>{item.name}</div>
              </div>
            );
          })}
        </div>
        {allItems.length > 8 && (
          <button onClick={() => toggleSection(key)} style={S.showMoreBtn}>
            {expanded ? "Show less" : `Show all ${allItems.length}`}
          </button>
        )}
      </section>
    );
  };

  /* ══════════════════════════════════════════════════════
     BOOKING PANEL
  ══════════════════════════════════════════════════════ */
  const BookingPanel = ({ compact = false }: { compact?: boolean }) => (
    <div style={{ background:T.white,borderRadius:20,border:`2px solid ${T.greenBorder}`,padding:compact?20:28,boxShadow:`0 8px 32px ${T.green}20` }}>
      {/* Rate */}
      <div style={{ display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:20 }}>
        <span style={{ fontSize:compact?22:28,fontWeight:800,color:T.black,fontFamily:T.serif }}>{rateLabel}</span>
        {avgRating && <span style={{ display:"flex",alignItems:"center",gap:4,fontSize:13,color:T.smoke }}><Star size={13} fill={T.green} color={T.green}/> {avgRating} ({reviewCount})</span>}
      </div>

      {/* rateType badge */}
      <div style={{ marginBottom:14 }}>
        <span style={{ display:"inline-block",padding:"4px 12px",borderRadius:999,background:T.greenSubtle,border:`1px solid ${T.greenBorder}`,fontSize:11,fontWeight:700,color:T.greenDark,letterSpacing:"0.05em" }}>
          {isPerHour?"Hourly rate":isPerDay?"Daily rate":isPerWeek?"Weekly rate":isPerMonth?"Monthly rate":"Yearly rate"}
        </span>
      </div>

      {/* Calendar */}
      <div style={{ background:T.offwhite,borderRadius:14,padding:16,marginBottom:16 }}>
        <CalendarTabs />
        {tab === "dates"  && (isDesktop ? renderDesktopCalendar() : renderMobileCalendar())}
        {tab === "hours"  && renderHoursTab()}
        {tab === "months" && renderMonthsTab()}
        {tab === "years"  && renderYearsTab()}
        {tab === "flex"   && renderFlexTab()}
      </div>

      {/* Selected slot display */}
  {/* Selected slot display */}
      {(checkIn || selectedTimes.length > 0) && (
        <>
          <div style={{ background:T.greenSubtle,borderRadius:12,padding:"10px 14px",marginBottom:6,fontSize:13,color:T.greenDark,fontWeight:700,border:`1px solid ${T.greenBorder}` }}>
            {isPerHour && selectedTimes.length > 0
              ? `${selectedTimes.length}h · ${selectedTimes[0]} → ${selectedTimes[selectedTimes.length-1]}`
              : startHour !== null && endHour !== null
              ? `${String(startHour).padStart(2,"00")}:00 → ${String(endHour).padStart(2,"00")}:00`
              : checkIn && checkOut ? `${dayjs(checkIn).format("MMM D")} → ${dayjs(checkOut).format("MMM D, YYYY")}` : checkIn
            }
          </div>
          {tab === "dates" && checkIn && checkOut && (
            <button onClick={() => { setCheckIn(null); setCheckOut(null); }}
              style={{ width:"100%",marginBottom:8,padding:"7px 0",borderRadius:10,border:`1px solid ${T.pearl}`,background:"transparent",fontWeight:600,fontSize:13,cursor:"pointer",color:T.smoke }}>
              ✕ Clear dates
            </button>
          )}
          {tab === "hours" && selectedTimes.length > 0 && (
            <button onClick={() => setSelectedTimes([])}
              style={{ width:"100%",marginBottom:8,padding:"7px 0",borderRadius:10,border:`1px solid ${T.pearl}`,background:"transparent",fontWeight:600,fontSize:13,cursor:"pointer",color:T.smoke }}>
              ✕ Clear hours
            </button>
          )}
        </>
      )}



      {/* Bedroom opener (replaces guests) */}
      <button onClick={() => setShowHoursModal(true)}
        style={{ width:"100%",padding:"12px 16px",borderRadius:12,border:`1px solid ${T.greenBorder}`,background:T.white,display:"flex",justifyContent:"space-between",alignItems:"center",cursor:"pointer",marginBottom:16,fontWeight:600,fontSize:14,color:T.black }}>
        <span>🏠 {bedroomPreview}</span>
        <span style={{ fontSize:12,color:T.green }}>Edit →</span>
      </button>

      {/* Warning */}
      {warningMsg && (
        <div style={{ background:"#fff3cd",border:"1px solid #ffc107",borderRadius:10,padding:"10px 14px",marginBottom:14,fontSize:13,fontWeight:600,color:"#856404",display:"flex",gap:8,alignItems:"center" }}>
          <AlertCircle size={16} color="#856404"/> {warningMsg}
        </div>
      )}

      {/* Unavailable banner */}
      {!available && (
        <div style={{ background:"#fdecea",borderRadius:12,padding:"10px 14px",marginBottom:14,fontSize:13,fontWeight:700,color:T.red,border:`1px solid ${T.red}40`,textAlign:"center" }}>
          These dates are unavailable
        </div>
      )}

      {/* CTA */}
      <button ref={reserveBtnRef} onClick={handleContinue}
        disabled={alreadyRequested || requestDeclined}
        style={{ width:"100%",padding:"16px 0",borderRadius:14,background:alreadyRequested||requestDeclined?"#e5e7eb":GREEN_GRAD,color:alreadyRequested||requestDeclined?T.smoke:T.white,border:"none",fontWeight:800,fontSize:16,cursor:alreadyRequested||requestDeclined?"not-allowed":"pointer",letterSpacing:"0.04em",boxShadow:alreadyRequested||requestDeclined?"none":`0 6px 24px ${T.green}50`,transition:"all .2s" }}>
        {reserveBtnLabel}
      </button>

      {isApproveMode && !alreadyRequested && !requestDeclined && (
        <p style={{ textAlign:"center",fontSize:12,color:T.smoke,marginTop:10 }}>You won't be charged yet</p>
      )}
    </div>
  );

  /* ═══════════════════════════════════════════════════
     MAIN RETURN
  ═══════════════════════════════════════════════════ */
  return (
    <div style={{ background:T.cream,minHeight:"100vh",fontFamily:T.sans }}>
      <GalleryLightbox />
      <HoursModal />
      <MissingModal />

      {/* STICKY NAV */}
      <div style={{ position:"fixed",top:0,left:0,right:0,zIndex:1000,background:scrolled?"rgba(255,255,255,0.97)":"transparent",backdropFilter:scrolled?"blur(12px)":"none",borderBottom:scrolled?`1px solid ${T.greenBorder}`:"none",transition:"all .3s",padding:"12px 24px",display:"flex",alignItems:"center",gap:16 }}>
        <button onClick={() => navigate(-1)}
          style={{ background:T.white,border:`1px solid ${T.greenBorder}`,borderRadius:"50%",width:40,height:40,display:"grid",placeItems:"center",cursor:"pointer",boxShadow:"0 2px 8px rgba(0,0,0,.1)" }}>
          <ArrowLeft size={18} color={T.black}/>
        </button>
        {scrolled && <span style={{ fontWeight:700,fontSize:16,color:T.black,fontFamily:T.serif }}>{listing?.title || "Cleaning Service"}</span>}
        <div style={{ marginLeft:"auto",display:"flex",gap:10 }}>
          <button onClick={() => { navigator.clipboard.writeText(window.location.href); setShareToast(true); }}
            style={{ background:T.white,border:`1px solid ${T.pearl}`,borderRadius:999,padding:"8px 16px",fontWeight:700,fontSize:13,cursor:"pointer",display:"flex",alignItems:"center",gap:6 }}>
            <Share2 size={15}/> Share
          </button>
          <button onClick={() => setLikedListing(l=>!l)}
            style={{ background:T.white,border:`1px solid ${T.pearl}`,borderRadius:999,padding:"8px 16px",fontWeight:700,fontSize:13,cursor:"pointer",display:"flex",alignItems:"center",gap:6 }}>
            <Heart size={15} fill={likedListing?T.red:"transparent"} color={likedListing?T.red:T.black}/> Save
          </button>
        </div>
      </div>

      {shareToast && (
        <div style={{ position:"fixed",top:72,left:"50%",transform:"translateX(-50%)",background:T.green,color:T.white,borderRadius:999,padding:"10px 24px",fontWeight:700,fontSize:14,zIndex:2000,boxShadow:"0 4px 20px rgba(0,0,0,.2)" }}>
          Link copied!
        </div>
      )}

      {/* PHOTO HERO */}
      <div style={{ paddingTop:64 }}>
        {photos.length > 0 ? (
          <div style={{ display:"grid",gridTemplateColumns:isDesktop?"2fr 1fr 1fr":"1fr",gap:4,maxHeight:isDesktop?520:300,overflow:"hidden" }}>
            <img src={photos[0]} alt="" onClick={() => { setGalleryIdx(0); setGalleryOpen(true); }}
              style={{ width:"100%",height:"100%",objectFit:"cover",cursor:"pointer",display:"block" }}/>
            {isDesktop && photos.slice(1,5).map((p,i) => (
              <img key={i} src={p} alt="" onClick={() => { setGalleryIdx(i+1); setGalleryOpen(true); }}
                style={{ width:"100%",height:i<2?260:256,objectFit:"cover",cursor:"pointer",display:"block" }}/>
            ))}
          </div>
        ) : (
          <div style={{ height:320,background:`linear-gradient(135deg,${T.greenDark},${T.green})`,display:"flex",alignItems:"center",justifyContent:"center" }}>
            <span style={{ color:"rgba(255,255,255,0.3)",fontSize:48 }}>🧹</span>
          </div>
        )}
        {photos.length > 5 && (
          <button onClick={() => setGalleryOpen(true)}
            style={{ position:"relative",marginTop:-50,marginLeft:"auto",marginRight:20,background:T.white,border:`1px solid ${T.pearl}`,borderRadius:10,padding:"8px 16px",fontWeight:700,fontSize:13,cursor:"pointer",display:"flex",alignItems:"center",gap:6 }}>
            <Grid size={14}/> Show all photos
          </button>
        )}
      </div>

      {/* BODY */}
      <div style={{ maxWidth:MAX_WIDTH,margin:"0 auto",padding:isDesktop?"40px 40px 80px":"20px 16px 120px",display:isDesktop?"grid":"block",gridTemplateColumns:"1fr 420px",gap:60,alignItems:"start" }}>

        {/* LEFT */}
        <div>
          {/* Title */}
          <div style={{ marginBottom:24 }}>
            {isGuestFavourite && (
              <div style={{ display:"inline-flex",alignItems:"center",gap:6,background:T.greenSubtle,border:`1px solid ${T.greenBorder}`,borderRadius:999,padding:"6px 14px",marginBottom:12,fontSize:13,fontWeight:700,color:T.greenDark }}>
                <Crown size={14} color={T.green}/> Guest Favourite
              </div>
            )}
            <h1 style={{ fontFamily:T.serif,fontSize:isDesktop?38:28,fontWeight:700,color:T.black,marginBottom:8,lineHeight:1.2 }}>
              {listing?.title || "Cleaning Service"}
            </h1>
            <div style={{ display:"flex",alignItems:"center",gap:12,flexWrap:"wrap" }}>
              <div style={{ display:"flex",alignItems:"center",gap:4,color:T.green,fontWeight:700,fontSize:14 }}>
                <MapPin size={15}/>{[listing?.address?.city,listing?.address?.country].filter(Boolean).join(", ")}
              </div>
              {avgRating && (
                <div style={{ display:"flex",alignItems:"center",gap:4,fontSize:14,color:T.black,fontWeight:700 }}>
                  <Star size={14} fill={T.green} color={T.green}/>{avgRating} · <span style={{ color:T.smoke,fontWeight:500 }}>{reviewCount} review{reviewCount!==1?"s":""}</span>
                </div>
              )}
            </div>
          </div>

          {/* Provider info bar */}
          <div style={{ display:"flex",gap:24,padding:"18px 0",borderTop:`1px solid ${T.pearl}`,borderBottom:`1px solid ${T.pearl}`,marginBottom:28,flexWrap:"wrap" }}>
            {[
              listing?.isCompany ? { icon:<Award size={20}/>, label:"Professional Company" } : { icon:<Users size={20}/>, label:"Individual Cleaner" },
              listing?.address?.city ? { icon:<MapPin size={20}/>, label:listing.address.city } : null,
              rateLabel ? { icon:<Zap size={20}/>, label:rateLabel } : null,
            ].filter(Boolean).map((f:any,i) => (
              <div key={i} style={{ display:"flex",alignItems:"center",gap:10 }}>
                <div style={{ color:T.green }}>{f.icon}</div>
                <span style={{ fontSize:14,fontWeight:600,color:T.black }}>{f.label}</span>
              </div>
            ))}
          </div>

          {/* Description */}
          <section style={S.section}>
            <h2 style={S.sectionTitle}>About this service</h2>
            {(listing?.description ?? "").split(/\n+/).map((p: string, i: number) => p.trim() && <p key={i} style={{ marginBottom:16,fontSize:16,lineHeight:1.8,color:T.black }}>{p}</p>)}
          </section>

          {/* Extras */}
          {listing?.extras && Object.keys(listing.extras).length > 0 && (
            <section style={S.section}>
              <h2 style={S.sectionTitle}>Add-on Services</h2>
              <div style={{ display:"flex",flexWrap:"wrap",gap:12 }}>
                {Object.entries(listing.extras).map(([name,price]:any) => {
                  const selected = selectedExtras.includes(name);
                  return (
                    <button key={name} onClick={() => toggleExtra(name)}
                      style={{ padding:"10px 18px",borderRadius:999,border:`1px solid ${selected?T.green:T.pearl}`,background:selected?T.greenSubtle:T.white,fontWeight:700,fontSize:13,cursor:"pointer",color:selected?T.greenDark:T.black,display:"flex",alignItems:"center",gap:8,transition:"all .2s" }}>
                      {selected && <Check size={14} color={T.green}/>}
                      {name} · {symbol}{Number(convertRaw(price,"EUR")).toLocaleString()}
                    </button>
                  );
                })}
              </div>
            </section>
          )}

          {/* Amenities */}
          {renderAmenitySection("amenities","What's included",cleaningAmenities,listing?.amenities||[])}
          {renderAmenitySection("safety","Safety & security",cleaningSafetyCategories,listing?.safety||[])}
          {renderAmenitySection("rules","Rules & preferences",cleaningRulesCategories,listing?.rules||[])}

          {/* Map */}
          {listing?.address?.latitude && listing?.address?.longitude && (
            <section style={S.section}>
              <h2 style={S.sectionTitle}>Location</h2>
              <div style={{ borderRadius:16,overflow:"hidden",height:320 }}>
                <GoogleMap
                  mapContainerStyle={{ width:"100%",height:"100%" }}
                  center={{ lat:listing.address.latitude,lng:listing.address.longitude }}
                  zoom={14}
                  options={{ disableDefaultUI:true,zoomControl:true }}>
                  <Marker position={{ lat:listing.address.latitude,lng:listing.address.longitude }}/>
                </GoogleMap>
              </div>
            </section>
          )}

          {/* Reviews */}
          {cleaningRatings.length > 0 && (
            <section style={S.section}>
              <h2 style={S.sectionTitle}>Reviews ({reviewCount})</h2>
              <div style={{ display:"grid",gridTemplateColumns:isDesktop?"1fr 1fr":"1fr",gap:16 }}>
                {cleaningRatings.slice(0,6).map((r:any,i:number) => (
                  <div key={i} style={{ padding:18,borderRadius:16,border:`1px solid ${T.pearl}`,background:T.white }}>
                    <div style={{ display:"flex",justifyContent:"space-between",marginBottom:8 }}>
                      {renderStars(r.listingRating)}
                      <span style={{ fontSize:12,color:T.smoke }}>{formatRelativeDate(r.dateCreated)}</span>
                    </div>
                    <p style={{ fontSize:14,lineHeight:1.7,color:T.black,margin:0 }}>"{r.guestReview}"</p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Host info */}
          <section style={S.section}>
            <h2 style={S.sectionTitle}>Meet your cleaner</h2>
            <div style={{ display:"flex",gap:20,alignItems:"flex-start",padding:20,background:T.white,borderRadius:16,border:`1px solid ${T.pearl}` }}>
              <div style={{ width:72,height:72,borderRadius:"50%",background:GREEN_GRAD,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,fontSize:28,color:T.white,fontWeight:700,fontFamily:T.serif }}>
                {host.firstName?.[0]?.toUpperCase()}
              </div>
              <div style={{ flex:1 }}>
                <div style={{ fontWeight:800,fontSize:18,color:T.black,fontFamily:T.serif }}>{host.firstName}</div>
                {avgRating && <div style={{ display:"flex",gap:6,alignItems:"center",marginTop:4 }}><Star size={14} fill={T.green} color={T.green}/><span style={{ fontSize:13,fontWeight:600,color:T.black }}>{avgRating}</span><span style={{ color:T.smoke,fontSize:13 }}>·</span><span style={{ fontSize:13,color:T.smoke }}>{reviewCount} review{reviewCount!==1?"s":""}</span></div>}
                <button onClick={() => navigate(`/imessagepreview/`)}
                  style={{ marginTop:14,padding:"10px 20px",borderRadius:999,background:GREEN_GRAD,border:"none",fontWeight:700,fontSize:13,cursor:"pointer",color:T.white,display:"flex",alignItems:"center",gap:8,boxShadow:`0 4px 16px ${T.green}40` }}>
                  <MessageCircle size={15}/> Message cleaner
                </button>
              </div>
            </div>
            <div style={{ marginTop:20 }}>
              <h3 style={{ fontWeight:700,marginBottom:12,fontSize:16,color:T.black }}>More from this cleaner</h3>
              <HostCleaningHomes hostId={host.hostId || ""} />
            </div>
          </section>
        </div>

        {/* RIGHT: DESKTOP BOOKING PANEL */}
        {isDesktop && (
          <div style={{ position:"sticky",top:80 }}>
            <BookingPanel />
          </div>
        )}
      </div>

      {/* MOBILE BOTTOM BOOKING BAR */}
{/* MOBILE — inline calendar card in page flow */}
      {isMobile && (
        <div style={{ padding: "0 16px 120px" }}>
          <div style={{
            background: T.white, borderRadius: 20,
            border: `2px solid ${T.greenBorder}`,
            padding: "20px 16px", marginBottom: 16,
            boxShadow: `0 8px 32px ${T.green}20`,
          }}>
            <div style={{ display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:16 }}>
              <span style={{ fontSize:22,fontWeight:800,color:T.black,fontFamily:T.serif }}>{rateLabel}</span>
              {avgRating && <span style={{ fontSize:13,color:T.smoke,display:"flex",alignItems:"center",gap:4 }}><Star size={13} fill={T.green} color={T.green}/> {avgRating} ({reviewCount})</span>}
            </div>
            <div style={{ display:"inline-block",padding:"4px 12px",borderRadius:999,background:T.greenSubtle,border:`1px solid ${T.greenBorder}`,fontSize:11,fontWeight:700,color:T.greenDark,letterSpacing:"0.05em",marginBottom:16 }}>
              {isPerHour?"Hourly":isPerDay?"Daily":isPerWeek?"Weekly":isPerMonth?"Monthly":"Yearly"} rate
            </div>
            <CalendarTabs />
            {tab === "dates"  && renderMobileCalendar()}
            {tab === "hours"  && renderHoursTab()}
            {tab === "months" && renderMonthsTab()}
            {tab === "flex"   && renderFlexTab()}
            {tab === "years"  && renderYearsTab()}
        {checkIn && checkOut && tab === "dates" && (
              <>
                <div style={{ marginTop:14,padding:"10px 14px",background:T.greenSubtle,borderRadius:10,fontWeight:700,color:T.greenDark,fontSize:13,textAlign:"center",border:`1px solid ${T.greenBorder}` }}>
                  {dayjs(checkIn).format("MMM D YYYY")} → {dayjs(checkOut).format("MMM D YYYY")} · {dayjs(checkOut).diff(dayjs(checkIn),"day")} days
                </div>
                <button onClick={() => { setCheckIn(null); setCheckOut(null); }}
                  style={{ marginTop:8,width:"100%",padding:"7px 0",borderRadius:10,border:`1px solid ${T.pearl}`,background:"transparent",fontWeight:600,fontSize:13,cursor:"pointer",color:T.smoke }}>
                  ✕ Clear dates
                </button>
              </>
            )}
            {selectedTimes.length > 0 && tab === "hours" && (
              <button onClick={() => setSelectedTimes([])}
                style={{ marginTop:8,width:"100%",padding:"7px 0",borderRadius:10,border:`1px solid ${T.pearl}`,background:"transparent",fontWeight:600,fontSize:13,cursor:"pointer",color:T.smoke }}>
                ✕ Clear hours
              </button>
            )}
            <button onClick={() => setShowHoursModal(true)}
              style={{ width:"100%",marginTop:14,padding:"12px 16px",borderRadius:12,border:`1px solid ${T.greenBorder}`,background:T.white,display:"flex",justifyContent:"space-between",alignItems:"center",cursor:"pointer",fontWeight:600,fontSize:14,color:T.black }}>
              <span>🏠 {bedroomPreview}</span>
              <span style={{ fontSize:12,color:T.green }}>Edit →</span>
            </button>
            {!available && (
              <div style={{ marginTop:12,padding:"10px 14px",borderRadius:10,background:"#fdecea",border:`1px solid ${T.red}40`,color:T.red,fontSize:13,fontWeight:700,textAlign:"center" }}>
                These dates are unavailable
              </div>
            )}
            {warningMsg && (
              <div style={{ marginTop:12,padding:"10px 14px",borderRadius:10,background:"#fff3cd",border:"1px solid #ffc107",color:"#856404",fontSize:13,fontWeight:600,display:"flex",gap:8,alignItems:"center" }}>
                <AlertCircle size={16} color="#856404"/> {warningMsg}
              </div>
            )}
          </div>
        </div>
      )}

      {/* MOBILE — sticky book button at bottom */}
      {isMobile && (
        <div style={{ position:"fixed",bottom:0,left:0,right:0,background:T.white,borderTop:`2px solid ${T.greenBorder}`,padding:"14px 16px 28px",zIndex:900 }}>
          <button ref={reserveBtnRef} onClick={handleContinue}
            disabled={alreadyRequested || requestDeclined}
            style={{ width:"100%",padding:"16px 0",borderRadius:14,background:alreadyRequested||requestDeclined?"#e5e7eb":GREEN_GRAD,color:alreadyRequested||requestDeclined?T.smoke:T.white,border:"none",fontWeight:800,fontSize:16,cursor:alreadyRequested||requestDeclined?"not-allowed":"pointer",letterSpacing:"0.04em",boxShadow:alreadyRequested||requestDeclined?"none":`0 6px 24px ${T.green}50` }}>
            {reserveBtnLabel}
          </button>
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════
   STYLES
══════════════════════════════════════════════ */
const S: Record<string, React.CSSProperties> = {
  section:     { marginBottom:40,paddingBottom:40,borderBottom:`1px solid ${T.pearl}` },
  sectionTitle:{ fontFamily:T.serif,fontSize:26,fontWeight:700,color:T.black,marginBottom:20,letterSpacing:"-0.01em" },
  amenGrid:    { display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(110px,1fr))",gap:12 },
  amenCard:    { padding:"16px 12px",borderRadius:14,border:"1px solid",textAlign:"center",background:T.white,transition:"all .2s" },
  showMoreBtn: { marginTop:16,padding:"10px 20px",borderRadius:999,border:`1px solid ${T.greenBorder}`,background:T.white,fontWeight:700,fontSize:13,cursor:"pointer",color:T.green },
  counterBtn:  { width:38,height:38,borderRadius:"50%",border:`1px solid ${T.greenBorder}`,background:T.white,fontSize:20,fontWeight:700,cursor:"pointer",display:"grid",placeItems:"center",color:T.black },
  hourInput:   { width:"100%",padding:"12px 14px",borderRadius:12,border:`2px solid ${T.greenBorder}`,fontSize:20,fontWeight:700,outline:"none",textAlign:"center" as const },
};